
import { Link } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';

export default function PartnershipCTA() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const partnershipTiers = [
    {
      name: 'Platinum',
      icon: 'ri-vip-crown-line',
      color: 'from-[#D4AF37] to-[#F4D03F]',
      benefits: ['Premier visibility', 'Speaking opportunity', 'Private dinner access'],
    },
    {
      name: 'Gold',
      icon: 'ri-medal-line',
      color: 'from-gray-400 to-gray-500',
      benefits: ['Strategic visibility', 'Branded presence', 'Networking reception'],
    },
    {
      name: 'Silver',
      icon: 'ri-award-line',
      color: 'from-gray-300 to-gray-400',
      benefits: ['Logo placement', 'Digital recognition', 'Event access'],
    },
  ];

  return (
    <section ref={sectionRef} className="py-24 bg-gradient-to-br from-[#F8F6F2] to-white relative overflow-hidden">
      {/* Decorative Elements */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-[#D4AF37]/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#E8B4B8]/5 rounded-full blur-3xl"></div>

      <div className="px-6 lg:px-16 relative z-10">
        <div className="max-w-6xl mx-auto">
          <div className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            {/* Header */}
            <div className="text-center mb-16">
              <div className="inline-block px-4 py-2 bg-[#D4AF37]/10 rounded-full mb-6">
                <p className="text-xs uppercase tracking-widest text-[#D4AF37] font-medium">Strategic Alliances</p>
              </div>
              <h2 className="text-4xl lg:text-5xl font-serif font-bold text-gray-900 mb-6">
                Partner With Us
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
                Join an exclusive network of partners aligned with the summit's vision of empowering women in wealth
              </p>
            </div>

            {/* Partnership Tiers Preview */}
            <div className={`grid grid-cols-1 md:grid-cols-3 gap-6 mb-12 transition-all duration-1000 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              {partnershipTiers.map((tier, index) => (
                <div
                  key={tier.name}
                  className="group bg-white p-6 rounded-2xl border border-gray-200 hover:border-[#D4AF37]/50 transition-all duration-500 hover:shadow-xl cursor-pointer"
                  style={{ transitionDelay: `${index * 100}ms` }}
                >
                  <div className={`w-12 h-12 flex items-center justify-center rounded-full bg-gradient-to-r ${tier.color} mb-4`}>
                    <i className={`${tier.icon} text-xl text-white`}></i>
                  </div>
                  <h3 className="text-xl font-serif font-bold text-gray-900 mb-3">{tier.name} Partner</h3>
                  <ul className="space-y-2">
                    {tier.benefits.map((benefit, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-gray-600">
                        <i className="ri-check-line text-[#D4AF37]"></i>
                        {benefit}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            {/* CTA Button */}
            <div className={`text-center transition-all duration-1000 delay-400 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              <Link
                to="/partners"
                className="inline-flex items-center gap-3 px-10 py-5 bg-[#1A1A2E] text-white rounded-full font-medium text-base hover:bg-[#2A2A3E] transition-all shadow-xl hover:shadow-2xl whitespace-nowrap cursor-pointer group"
              >
                <i className="ri-handshake-line text-xl"></i>
                Become a Partner
                <i className="ri-arrow-right-line text-xl group-hover:translate-x-1 transition-transform"></i>
              </Link>
              <p className="mt-4 text-sm text-gray-500">
                Explore partnership tiers and benefits
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
